import { useEffect, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";

import { CardMedia } from "@mui/material";
import AppCard from "@plannel/components/ui/AppCard";
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip"
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import { green, grey, red } from '@mui/material/colors';

import { withTranslation } from "react-i18next";
import { isEqual, isNull, isUndefined, includes, isEmpty } from "lodash";
import { SwiperSlide } from "swiper/react";

import rpCompService from "@plannel/services/rp/rp-comp-service";
import planVersionService from "@plannel/services/data/plan-version-service";
import userService from "@plannel/services/system/user-service";
import planConfigService from "@plannel/services/data/plan-config-service";

import GridUtils from "@plannel/components/aggrid/GridUtils";
import RatioBarCell from "@plannel/components/aggrid/RatioBarCell";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import ScenarioReleaseModal from "@plannel/components/modal/ScenarioReleaseModal";
import UnconfirmedScenarioNotice, { isUnconfirmedScenarioVersion } from "@plannel/components/UnconfirmedScenarioNotice";
import Dialog from "@plannel/components/Dialog";
import ChartComposed from "@plannel/components/rechart/ChartComposed";
import Snackbar from "@plannel/components/Snackbar";
import { LockButton, UnlockButton } from "@plannel/components/ActionIconButton";
import NumberUtil from "@plannel/utils/number-util";
import dateUtils from "@plannel/utils/date-util";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import axiosUtil from "@plannel/utils/axios-bigint";
import reduxUtil from "@plannel/utils/redux-util";
import codeUtils from "@plannel/pages/utils/code-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import ToggleButton from "@plannel/components/ToggleButton";
import {useDispatch} from "react-redux";
import comparisonComponent from "@plannel/components/layout/ComparisonContainer";
import { ComparisonReleaseBar, SelectableComparison } from "@plannel/components/layout/ComparisonSelection";
import { FS } from "@plannel/utils/font-util";

const { versionStatusCode } = codeUtils;
const { SCENARIO, RELEASED, RELEASING } = versionStatusCode
const { snackBarMessageColor } = ColorUtil;
const { roles, module } = codeUtils;
const { DP } = module;

const userInfo = axiosUtil.jsonbigParse(localStorage.getItem("user"));

const RpCompHeader = (props) => {
  const { index, rpSummary, updateVersionDelFlg, isReleasing = false } = props;
  const { planVerName, planVersion } = rpSummary;

  const setToolTip = version => {
    if (version?.planConfigObj) {
      const { PLAN_START_DATE: startDate, BUCKET_UNIT: unit, PLANNING_RANGE: range, START_DAY: startDay } = version.planConfigObj;
      let format = "yyyy-MM";
      if (codeUtils.isWeekBucket(unit)) {
        format = "yyyy-MM-dd";
      }
      return `${dateUtils.formatDate(startDate, format)} ~
      ${dateUtils.formatDate(dateUtils.calculateRangeDate(startDate, unit, startDay, range, true), format)}`;
    }
    return null;
  };

  return (
    <>
      <Box className="comparison-header" sx={{ display: "flex", height: "55px", flexShrink: 0, alignItems: "flex-end", justifyContent: "center" }}>
        <LeaderboardIcon fontSize={"large"} sx={{ mb: 0.2, color: index === 0 ? green[600] : red[500] }} />
        <Tooltip title={setToolTip(planVersion)} placement="top">
          <Typography variant="subtitle1" sx={{ pl: 1, textAlign: "center", fontWeight: 500 }}>
            {planVerName}
          </Typography>
        </Tooltip>
      </Box>
      {
        isEqual(planVersion.statusCd, RELEASED) ? null : (
          <Box sx={{ position: "absolute", top: "37px", right: "15px" }}>
            {planVersion.delFlg ?
              <UnlockButton
                label="lock"
                disabled={isEqual(planVersion?.statusCd, RELEASING) || isReleasing}
                onClick={() => updateVersionDelFlg(planVersion)}
              />
              : <LockButton label="unlock"
                disabled={isEqual(planVersion?.statusCd, RELEASING) || isReleasing}
                onClick={() => updateVersionDelFlg(planVersion)}
              />}
          </Box>
        )
      }
    </>
  );
}

const RpCompChart = (props) => {
  const { t, rpSummary, dataMax, chartSeries } = props;
  const { rpSummaryChartData } = rpSummary;

  return (
    <Box className="comparison-chart" sx={{ height: "calc(60% - 100px)", flexShrink: 0 }}>
      {
        rpSummaryChartData !== null && rpSummaryChartData.length > 0 ?
          <ChartComposed
            xAxis={"planDate"}
            dataSeries={chartSeries}
            data={rpSummaryChartData}
            options={{
              minWidth: "33%",
              xAxisHeight: 80,
              xAxisInterval: null,
              minHeight: 210,
              tooltipContentStyle: { fontSize: FS.SUB },
              yAxisDomain: [0, dataMax]
            }}
            showConfigurationPopup={false}
          /> :
          <Box sx={{ display: "flex", height: "200px", alignItems: "center", justifyContent: "center" }}>
            <Typography variant="subtitle1" sx={{ p: 3, textAlign: "center", fontWeight: 500, borderColor: grey[900], borderStyle: "solid" }}>
              {t("no chart data")}
            </Typography>
          </Box>
      }
    </Box>
  );
}

const RpCompSummary = (props) => {
  const { t, title, rpSummary } = props;
  const userData = JSON.parse(localStorage.getItem("user"));
  const symbol = userData.currencySymbol;

  const fillRate = NumberUtil.formatNumber(rpSummary.fillRate, { maximumFractionDigits: 1 });
  const formatProfit = NumberUtil.formatNumber(rpSummary.profit);
  let profit = symbol + formatProfit;
  if (rpSummary.profit < 0) {
    profit = `-${symbol}${formatProfit.replace("-", "")}`;
  }
  const numberFormatter = ({ data }) => {
    const value = data.value;

    if (data.category === "period") {
      return value;
    }

    if (isNull(value) || isUndefined(value)) {
      return "";
    }

    const formatNumber = NumberUtil.formatNumber(value);

    if (data.category === "purchasePlanQty" || data.category === "replenishPlanQty") {
      return formatNumber;
    }

    if (value < 0) {
      return `-${symbol}${formatNumber.replace("-", "")}`;
    }
    return symbol + formatNumber;
  }

  const percentFormatter = params => {
    const value = NumberUtil.formatNumberData(params, { maximumFractionDigits: 1 });

    return `${value}%`;
  }

  // 릴리즈 버튼은 화면 상단 공통 바로 빠졌다. 카드에는 상태만 남는다.
  // "릴리즈 중"도 상단 바가 보여 주므로 카드에서는 그리지 않는다.
  const ReleaseStatus = ({ rpSummary }) => {
    if (isEqual(rpSummary.planVersion.statusCd, RELEASED)) {
      return (
          <AppCard sx={{display: "flex", width: "100%", height: "40px", minHeight: "40px", p: 0, mt: 1}}>
            <CardMedia sx={{display: "flex", alignItems: "center", width: "50%", bgcolor: green[700]}}>
              <Typography variant="subtitle1" pl="10px" color="#fff">
                {`${t("released")}`}
              </Typography>
            </CardMedia>
            <Box sx={{display: "flex", width: "50%", alignItems: "center", textAlign: "right"}}>
              <Typography
                  variant="subtitle1"
                  component="span"
                  ml="auto"
                  width="50%"
                  textAlign="right"
                  pr="10px"
              >
                {`${dateUtils.formatDate(rpSummary.releaseTs, "yyyy-MM-dd")}`}
              </Typography>
            </Box>
          </AppCard>
      )
    } else if (isUnconfirmedScenarioVersion(rpSummary.planVersion)) {
      // 확정 전 시나리오 플래닝 버전은 릴리즈할 수 없다. 확정 화면으로 보낸다.
      return <UnconfirmedScenarioNotice moduleCd="RP" />;
    }
    return null;
  }

  const colProdDefs = [
    { headerName: "realCategory", field: "category", flex: 2, minWidth: 180, valueFormatter: ({ value }) => t(value) },
    {
      headerName: "", field: "rate", valueGetter: ({ data }) => data.rate,
      cellRenderer: RatioBarCell,
      cellRendererParams: { formatter: percentFormatter },
      flex: 2,
      minWidth: 80
    },
    { headerName: "value", flex: 2, minWidth: 180, field: "value", type: ["rightAligned"], valueFormatter: numberFormatter }
  ];

  const getContextMenuItems = (params) => {
    return params.defaultItems.filter(
      (item) => item !== 'export'
    );
  };

  return (
    // 그리드가 남는 높이를 먹도록 이 블록도 flex 로 둔다. 릴리즈 버튼이 빠진 자리가 빈 공간으로 남지 않게.
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Box sx={{ height: "90px", flexShrink: 0 }}>
        <Box sx={{ width: "100%", height: "40px" }}>
          <AppCard sx={{ display: "flex", height: "100%", minHeight: "30px", p: 0 }}>
            <CardMedia
              sx={{ display: "flex", alignItems: "center", width: "50%", bgcolor: "#048BA8" }}
            >
              <Typography variant="subtitle1" pl="10px" color="#fff">
                {t("fillRate")}
              </Typography>
            </CardMedia>
            <Box sx={{ display: "flex", width: "50%", alignItems: "center", textAlign: "right" }}>
              <Typography
                variant="subtitle1"
                component="span"
                ml="auto"
                width="50%"
                textAlign="right"
                pr="10px"
              >
                {`${fillRate}%`}
              </Typography>
            </Box>
          </AppCard>
        </Box>
        <Box sx={{ width: "100%", height: "40px", mt: 1 }}>
          <AppCard sx={{ display: "flex", height: "100%", minHeight: "30px", p: 0 }}>
            <CardMedia sx={{ display: "flex", alignItems: "center", width: "50%", bgcolor: "#048BA8" }}>
              <Typography variant="subtitle1" pl="10px" color="#fff">
                {t("expectedProfit")}
              </Typography>
            </CardMedia>
            <Box sx={{ display: "flex", width: "50%", alignItems: "center", textAlign: "right" }}>
              <Typography
                variant="subtitle1"
                component="span"
                ml="auto"
                textAlign="right"
                pr="10px"
              >
                {profit}
              </Typography>
            </Box>
          </AppCard>
        </Box>
      </Box>
      <Box className="ag-theme-balham" sx={{ flex: 1, minHeight: 0, mt: 1 }} >
        <AgGridReact
          rowData={rpSummary.rpCompGridData}
          columnDefs={GridUtils.getColumnDefs(colProdDefs)}
          {...DefaultGridSetting({ title })}
          defaultColDef={{
            flex: 1,
            minWidth: 30,
            suppressHeaderMenuButton: true,
            resizable: true
          }}
          getContextMenuItems={getContextMenuItems}
          groupDefaultExpanded={-1}
          groupHideOpenParents
        />
      </Box>
      <Box sx={{ width: "100%", flexShrink: 0 }}>
        <ReleaseStatus rpSummary={rpSummary} />
      </Box>
    </Box>
  );
}

const RpScenarioCompDashboard = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const { toggleType: { AMT, QTY } } = codeUtils;
  const [measureType, setMeasureType] = useState(reduxViewState?.measureType ?? AMT);

  const [releaseOpen, setReleaseOpen] = useState(false);
  const [rpCompData, setRpCompData] = useState([]);
  const selectedVersion = useRef({});
  const [dataMax, setDataMax] = useState("auto");
  const [amtDataMax, setAmtDataMax] = useState("auto");
  const [dpVersions, setDpVersions] = useState([]);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [userRoles, setUserRoles] = useState([]);
  const [isReleasing, setIsReleasing] = useState(false);
  const [updateOrderMethod, setUpdateOrderMethod] = useState(false);
  const [dpVersionWarnOpen, setDpVersionWarnOpen] = useState(false);

  // 카드에서 고른 버전 — 릴리즈는 상단 공통 바에서 이 버전 하나로만 실행한다.
  const [selectedCardId, setSelectedCardId] = useState(null);
  const selectedSummary = rpCompData?.find(rpSummary => isEqual(rpSummary?.planVerId, selectedCardId)) ?? null;
  const releasable = !!selectedSummary
    && !isEqual(selectedSummary.planVersion?.statusCd, RELEASED)
    && !isEqual(selectedSummary.planVersion?.statusCd, RELEASING)
    && !isUnconfirmedScenarioVersion(selectedSummary.planVersion);

  // 버튼이 회색인 이유를 툴팁으로 알려 준다.
  const releaseBlockReason = !selectedSummary || releasable
    ? null
    : isUnconfirmedScenarioVersion(selectedSummary.planVersion)
      ? t("MSG_ScenarioPlanningNotConfirmed")
      : t("MSG_NoVersionToRelease");

  const chartSeries = [
    {
      id: "purchaseQty",
      field: "purchaseQty",
      text: t("purchasePlanQty"),
      type: "bar",
      typeOptions: { stackId: "rpBar", fill: ColorUtil.colorSet(0), barSize: 20 },
      orientation: "left",
      groupName: t("qty")
    },
    {
      id: "rpOrderQty",
      field: "rpOrderQty",
      text: t("replenishPlanQty"),
      type: "bar",
      typeOptions: { stackId: "rpBar", fill: ColorUtil.colorSet(1), barSize: 20 },
      orientation: "left",
      groupName: t("qty")
    },
    {
      id: "boh",
      field: "boh",
      text: t("boh"),
      type: "line",
      typeOptions: { stroke: ColorUtil.colorSet(2), strokeWidth: 2 },
      orientation: "right",
      groupName: t("qty")
    },
    {
      id: "eoh",
      field: "eoh",
      text: t("eoh"),
      type: "line",
      typeOptions: { stroke: ColorUtil.colorSet(3), strokeWidth: 2 },
      orientation: "right",
      groupName: t("qty")
    },
  ];

  const amtChartSeries = [
    {
      id: "purchaseAmt",
      field: "purchaseAmt",
      text: t("purchasePlanAmt"),
      type: "bar",
      typeOptions: { stackId: "rpBar", fill: ColorUtil.colorSet(0), barSize: 20 },
      orientation: "left",
      groupName: t("amt")
    },
    {
      id: "rpOrderAmt",
      field: "rpOrderAmt",
      text: t("replenishPlanAmt"),
      type: "bar",
      typeOptions: { stackId: "rpBar", fill: ColorUtil.colorSet(1), barSize: 20 },
      orientation: "left",
      groupName: t("amt")
    },
    {
      id: "boh",
      field: "bohAmt",
      text: t("boh"),
      type: "line",
      typeOptions: { stroke: ColorUtil.colorSet(2), strokeWidth: 2 },
      orientation: "right",
      groupName: t("amt")
    },
    {
      id: "eoh",
      field: "eohAmt",
      text: t("eoh"),
      type: "line",
      typeOptions: { stroke: ColorUtil.colorSet(3), strokeWidth: 2 },
      orientation: "right",
      groupName: t("amt")
    },
  ];

  const setResultData = (rpSummaryData) => {
    for (const data of rpSummaryData) {
      const summaryDataList = [];

      const versionConfig = data?.planVersion?.planConfigObj;
      let period = "";
      if (versionConfig) {
        const { BUCKET_UNIT, PLAN_START_DATE, PLAN_END_DATE } = versionConfig;
        const startDate = dateUtils.headerDateFormatter(PLAN_START_DATE, BUCKET_UNIT);
        const endDate = dateUtils.headerDateFormatter(PLAN_END_DATE, BUCKET_UNIT);

        period = `${startDate} ~ ${endDate}`;
      }

      summaryDataList.push({ category: "period", rate: null, "value": period  });
      // summaryDataList.push({ category: "purchasePlanQty", rate: null, "value": data.purchasePlanQty });
      summaryDataList.push({ category: "purchasePlanAmt", rate: null, "value": data.purchasePlanAmt });
      // summaryDataList.push({ category: "replenishPlanQty", rate: null, "value": data.rpPlanQty });
      summaryDataList.push({ category: "replenishPlanAmt", rate: null, "value": data.rpPlanAmt });
      summaryDataList.push({ category: "revenue", rate: data.revenueRate, "value": data.revenue });
      summaryDataList.push({ category: "cost", rate: data.costRate, "value": data.cost });
      summaryDataList.push({ category: "profit", rate: data.profitRate, "value": data.profit });

      data.rpCompGridData = summaryDataList;
    }

    setRpCompData(rpSummaryData);
  }

  const retrieveRpComp = () => {
    rpCompService.rpSummaries()
      .then(response => {
        const rpSummaryData = response.data;
        if (!isEmpty(rpSummaryData)) {
          setResultData(rpSummaryData);
          const qtyArrays = [];
          const amtArrays = [];

          const hasReleasingVersion = rpSummaryData.some(
            scenario => isEqual(scenario.planVersion.statusCd, RELEASING)
          );
          setIsReleasing(hasReleasingVersion);

          if (rpSummaryData) {
            const qtyNames = ["bohQty", "eohQty", "rpOrderQty", "purchaseQty", "boh", "eoh"];
            const amtNames = ["bohAmt", "eohAmt", "rpOrderAmt", "purchaseAmt"];

            for (const rpSumData of rpSummaryData) {
              if (rpSumData?.rpSummaryChartData) {
                for (const rpSuChartData of rpSumData?.rpSummaryChartData) {
                  for (const qtyName of qtyNames) {
                    if (rpSuChartData[qtyName]) {
                      qtyArrays.push(rpSuChartData[qtyName]);
                    }
                  }
                  for (const amtName of amtNames) {
                    if (rpSuChartData[amtName]) {
                      amtArrays.push(rpSuChartData[amtName]);
                    }
                  }
                }
              }
            }
            const maxValue = NumberUtil.getMaxValueForChart(qtyArrays);
            const amtMaxValue = NumberUtil.getMaxValueForChart(amtArrays);

            setDataMax(maxValue);
            setAmtDataMax(amtMaxValue);
          }
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoVersion") });
        }
      })
      .catch(e => {
        debug(e);
      });
  }

  const updateVersionDelFlg = planVersion => {
    const versionId = { planVersionId: planVersion?.id };
    planVersionService.delFlg(versionId)
      .then(response => {
        const updatedVersion = response.data;

        const datas = [];
        for (const data of rpCompData) {
          if (isEqual(updatedVersion.id, data.planVerId)) {
            data.planVersion = updatedVersion;
          }
          datas.push(data);
        }

        setRpCompData(datas);
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t(e.response.data) });
        debug(e);
      });
  }

  const onClickReleaseButton = rpSummary => {
    selectedVersion.current = rpSummary;
    setReleaseOpen(true);
  }

  // 상위 DP 버전이 아직 릴리즈되지 않았으면 먼저 확인받는다(DP 권한이 있는 사용자만 따진다).
  const handleReleaseClick = () => {
    if (!selectedSummary) {
      return;
    }
    const dpVersionId = selectedSummary.planVersion?.planConfigObj?.DP_VERSION_ID;
    const dpVersion = dpVersionId ? dpVersions?.find(version => isEqual(version.id, dpVersionId)) : null;

    if (includes(userRoles, roles.APP_DP) && !isEqual(dpVersion?.statusCd, RELEASED)) {
      setDpVersionWarnOpen(true);
    } else {
      onClickReleaseButton(selectedSummary);
    }
  }

  const handleWarnDialog = action => {
    if (action === "WARNING") {
      onClickReleaseButton(selectedSummary);
    }
  }

  const retrieveUserRoles = () => {
    userService.getUserAccessRoles({ username: userInfo.username })
      .then(
        (response) => {
          const userAccess = response.data;
          setUserRoles(userAccess?.roles);
        },
      )
      .catch(e => { debug(e); });
  };

  const retrieveDpVersion = () => {
    planVersionService.getAll({ moduleCd: DP, statusCds: [RELEASED, SCENARIO], orderByColumn: "updatedTs", sortType: "desc" })
      .then(response => {
        const { results } = response.data;
        setDpVersions(results);
      })
      .catch(e => {
        debug(e);
      });
  }

  const retrieveRpPlanConfig = () => {
    planConfigService.getAll({ moduleCd: codeUtils.module.RP })
      .then(response => {
        const { data } = response;
        for (const item of data.results) {
          if (item.configCd === "RP_SETTINGS_CONFIG") {
            const { objVal } = item;
            if (objVal && objVal?.UPDATE_ORDER_METHOD !== undefined) {
              setUpdateOrderMethod(objVal.UPDATE_ORDER_METHOD);
            }
          }
        }
      })
      .catch(e => {
        debug(e);
      });
  }

  useEffect(() => {
    if (!isEmpty(userRoles)) {
      retrieveDpVersion();
      retrieveRpComp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRoles]);

  useEffect(() => {
    const reduxState = reduxUtil.createObjectState(viewName, { measureType });
    reduxDispatch(reduxUtil.updateViewState(reduxState));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measureType]);

  useEffect(() => {
    retrieveUserRoles();
    retrieveRpPlanConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <Box className={"wrap-comparion-dashboard display-block"}>
        <FilterContainer>
          <ToggleButton styles={{ mb: 0.5 }} toggleType="measure" initialValue={measureType} toggleValue={setMeasureType}></ToggleButton>
          <ComparisonReleaseBar
            selectedName={selectedSummary?.planVerName}
            disabled={!releasable}
            disabledReason={releaseBlockReason}
            isReleasing={isReleasing}
            onRelease={handleReleaseClick}
            sx={{ width: "auto", py: 0 }}
          />
        </FilterContainer>
        <comparisonComponent.ComparisonContainer>
          {
            rpCompData?.map((rpSummary, idx) => (
              <SwiperSlide key={rpSummary.planVerId}>
                <SelectableComparison
                  selected={isEqual(rpSummary.planVerId, selectedCardId)}
                  onSelect={() => setSelectedCardId(prev => (isEqual(prev, rpSummary.planVerId) ? null : rpSummary.planVerId))}>
                  <Box sx={{
                    // 100vh 계산을 쓰지 않는다 — 실제 가용 높이는 #tab-body 가 정하고 뷰포트보다 작다(App.scss 주석).
                    height: "100%",
                    // 전역 border-box 가 없는 앱이다. content-box 로 두면 위아래 10px 패딩이 높이에 더해져
                    // 카드가 제 칸보다 20px 커지고 맨 아래 상태 배지가 잘린다.
                    boxSizing: "border-box",
                    p: "10px 0",
                    // 그리드가 남는 높이를 먹게 한다. 릴리즈 버튼이 빠진 자리가 빈 공간으로 남지 않도록.
                    display: "flex",
                    flexDirection: "column",
                  }}>
                    <RpCompHeader index={idx} rpSummary={rpSummary} updateVersionDelFlg={updateVersionDelFlg} isReleasing={isReleasing} />
                    <RpCompChart t={t} chartSeries={isEqual(measureType, QTY) ? chartSeries : amtChartSeries} rpSummary={rpSummary} dataMax={isEqual(measureType, QTY) ? dataMax : amtDataMax} />
                    <RpCompSummary
                      t={t}
                      title={title}
                      rpSummary={rpSummary}
                    />
                  </Box>
                </SelectableComparison>
              </SwiperSlide>
            ))
          }
        </comparisonComponent.ComparisonContainer>
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Dialog
        open={dpVersionWarnOpen}
        title="warning"
        content={t("MSG_NotReleasedDpVersionConfirm")}
        onHandler={handleWarnDialog}
        onClose={() => setDpVersionWarnOpen(false)}
      />
      <ScenarioReleaseModal
        open={releaseOpen}
        close={setReleaseOpen}
        setIsReleasing={setIsReleasing}
        reload={retrieveRpComp}
        moduleCd={codeUtils.module.RP}
        service={rpCompService}
        selectScenario={selectedVersion?.current ?? {}}
        nameProperty={"planVerName"}
        updateOrderMethod={updateOrderMethod}
      />
    </>
  );
};

export default withTranslation()(RpScenarioCompDashboard);
